import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Upload, Icon, Row, Col, Select, Form, Result, message} from 'antd';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';
import { ModalBody, ModalContainer, InputLabel, Container, UploadContainer } from './styles';
import { uploadFonts } from '../../utils/services/uploadAPI';
import { getGoogleFonts, uploadGoogleFonts } from '../../utils/services/fontsAPI';
import { FONTS_UPLOAD_MODAL } from './translationValues';

const { Option } = Select;
const NAMESPACES = ['FONTS_UPLOAD_MODAL'];

const UploadFontModal = props => {
  const { tabIndex, onCancel, onSucess, fontList } = props;
  const { t } = useTranslation(NAMESPACES);
  const [loader, setLoader] = useState(false);
  const [fontName, setFontName] = useState('');
  const [uploadedFonts, setUploadedFonts] = useState({});
  const [gFonts, setGFonts] = useState([]);
  const [filteredFonts, setFilterdFonts] = useState([]);
  const [selectedFonts, setSelectedFonts] = useState([]);
  const [searchText, setSearchText] = useState('');
  // the fonts already in application
  const [existingFonts, setexistingFonts] = useState([]);
  const [duplicate, setDuplicate] = useState(false);
  const [uploadCompleted, setUpload] = useState(false);

  useEffect(() => {
    (async () => {
      let googleFonts = await getGoogleFonts();
      let fonts = [];
      let fontNames = _.pluck(fontList, 'name');
      for (const data of googleFonts) {
        if (!fontNames.some(font => font === data.family)) {
          fonts.push(data);
        }
      }
      setGFonts(fonts);
      setexistingFonts(fontNames);
      const topFonts = fonts.slice(0, 50);
      setFilterdFonts(topFonts);
    })();
  }, [fontList]);

  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  const handleChange = (data, keyType) => {
    let { file } = data;
    if (keyType === 'fontRegular' || fontName.length === 0) {
      let name = file?.name;
      name = name.split('.')[0];
      const event = {
        target: {
          value: name,
        },
      };
      handleFontName(event);
      setFontName(name);
    }
    let updatedFonts = { ...uploadedFonts, [keyType]: file.originFileObj };
    setUploadedFonts(updatedFonts);
  };

  const getBoldAndItalic = (file, type = '') => {
    return file[`700${type}`] || file[`800${type}`] || file[`600${type}`] || file[`500${type}`];
  };

  const constructGoogleFontJson = () => {
    let allFonts = [];
    for (let font of selectedFonts) {
      font = JSON.parse(font);
      let { family, files } = font;
      let { regular, italic } = files;
      let fontVariants = {
        Regular: regular,
        Italic: italic,
        Bold: getBoldAndItalic(files),
        BoldItalic: getBoldAndItalic(files, 'italic'),
      };
      for (const type of Object.keys(fontVariants)) {
        if (fontVariants[type]) {
          let fontObj = {
            fontName: family,
            fontType: type,
            file: fontVariants[type],
          };
          allFonts.push(fontObj);
        }
      }
    }
    return allFonts;
  };

  const saveFonts = async () => {
    let response;
    setLoader(true);
    if (tabIndex === 1) {
      let payload = {
        fonts: constructGoogleFontJson(),
      };
      response = await uploadGoogleFonts(payload);
    } else {
      response = await uploadFonts(uploadedFonts, fontName);
    }
    if (response) {
      setLoader(false);
      if (
        ['File Upload Succesfully', 'GoogleFonts Upload Successfully'].includes(response?.status)
      ) {
        setUpload(true);
        onSucess();
        message.success(response.status);
      } else {
        setLoader(false);
        onCancel();
      }
    } else {
      setLoader(false);
    }
  };

  const getTopFonts = () => {
    if (gFonts.length > 0) {
      const topFonts = gFonts.slice(0, 50);
      setFilterdFonts(topFonts);
    }
  };

  const onFontSearch = text => {
    let searchedFonts = [];
    if (text.length === 0) {
      getTopFonts();
    }
    for (const font of gFonts) {
      if (font.family.toLowerCase().includes(text.toLowerCase())) {
        searchedFonts.push(font);
      }
    }
    setSearchText(text);
    setFilterdFonts(searchedFonts);
  };

  const onScroll = e => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollTop + clientHeight + 50 >= scrollHeight) {
      if (searchText.length === 0) {
        const nextPage = gFonts.slice(filteredFonts.length, filteredFonts.length + 50);
        setFilterdFonts([...filteredFonts, ...nextPage]);
      }
    }
  };

  const handleFontName = e => {
    const { value } = e.target;
    setFontName(e.target.value);
    if (existingFonts.some(font => font.toLowerCase() === value.toLowerCase())) {
      setDuplicate(true);
    } else {
      setDuplicate(false);
    }
  };

  const UploadView = (title, keyType) => {
    return (
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <InputLabel>{title}</InputLabel>
        </Col>
        <Col span={8}>
          <Upload
            customRequest={customRequest}
            showUploadList={false}
            onChange={file => handleChange(file, keyType)}
            multiple={false}
            accept={'.ttf,.woff,.wof'}
          >
            <Button>
              <Icon type="upload" /> {t(FONTS_UPLOAD_MODAL.UPLOAD_FONT_BUTTON.key, FONTS_UPLOAD_MODAL.UPLOAD_FONT_BUTTON.value)}
            </Button>
          </Upload>
        </Col>
        <Col span={10}>{uploadedFonts[keyType]?.name}</Col>
      </Row>
    );
  };

  const renderUploadView = () => {
    return (
      <UploadContainer>
        <Form>
          <Form.Item
            validateStatus={duplicate ? 'error' : ''}
            help={duplicate ? t(FONTS_UPLOAD_MODAL.FONT_EXIST_WARNING_MESSAGE.key, FONTS_UPLOAD_MODAL.FONT_EXIST_WARNING_MESSAGE.value) : ''}
          >
            <InputLabel>{t(FONTS_UPLOAD_MODAL.FONT_NAME_LABEL.key, FONTS_UPLOAD_MODAL.FONT_NAME_LABEL.value)}</InputLabel>
            <Input
              placeholder={t(FONTS_UPLOAD_MODAL.FONT_NAME_PLACEHOLDER.key, FONTS_UPLOAD_MODAL.FONT_NAME_PLACEHOLDER.value)}
              autoComplete="off"
              name="fontName"
              onChange={handleFontName}
              size="large"
              value={fontName}
            />
          </Form.Item>
        </Form>
        {UploadView(t(FONTS_UPLOAD_MODAL.REGULAR_FONT_STYLE.key, FONTS_UPLOAD_MODAL.REGULAR_FONT_STYLE.value), 'fontRegular')}
        {UploadView(t(FONTS_UPLOAD_MODAL.Bold_FONT_STYLE.key, FONTS_UPLOAD_MODAL.Bold_FONT_STYLE.value), 'fontBold')}
        {UploadView(t(FONTS_UPLOAD_MODAL.Italic_FONT_STYLE.key, FONTS_UPLOAD_MODAL.Italic_FONT_STYLE.value), 'fontItalic')}
        {UploadView(t(FONTS_UPLOAD_MODAL.Bold_Italic_FONT_STYLE.key, FONTS_UPLOAD_MODAL.Bold_Italic_FONT_STYLE.value), 'fontBoldItalic')}
      </UploadContainer>
    );
  };

  const renderGoogleFonts = () => {
    return (
      <UploadContainer>
        <Select
          showSearch
          mode="multiple"
          size="large"
          style={{ width: '100%' }}
          placeholder={t(FONTS_UPLOAD_MODAL.SELECT_FONTS_PLACEHOLDER.key, FONTS_UPLOAD_MODAL.SELECT_FONTS_PLACEHOLDER.value)}
          defaultValue={selectedFonts}
          onSearch={onFontSearch}
          onPopupScroll={onScroll}
          onSelect={() => setSearchText('')}
          onDeselect={() => setSearchText('')}
          onBlur={() => {
            setSearchText('');
            getTopFonts();
          }}
          onChange={fonts => [setSelectedFonts(fonts), getTopFonts()]}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {filteredFonts.map((data, index) => (
            <Option value={JSON.stringify(data)} key={data.family + index}>
              {data.family}
            </Option>
          ))}
        </Select>
      </UploadContainer>
    );
  };

  let enabled =
    tabIndex === 1
      ? selectedFonts.length > 0
      : fontName.length > 0 && Object.keys(uploadedFonts).length > 0;
  return (
    <Container onKeyDown={e => e.stopPropagation()} style={{ position: 'absolute' }}>
      <Modal
        title={tabIndex === 1 ? t(FONTS_UPLOAD_MODAL.ADD_GOOGLE_FONTS_MODAL_TITLE.key, FONTS_UPLOAD_MODAL.ADD_GOOGLE_FONTS_MODAL_TITLE.value) : t(FONTS_UPLOAD_MODAL.ADD_CUSTOME_FONTS_MODAL_TITLE.key, FONTS_UPLOAD_MODAL.ADD_CUSTOME_FONTS_MODAL_TITLE.value)}
        centered    
        maskClosable={false}
        visible={true}
        width="825px"
        onCancel={onCancel}
        footer={
          !uploadCompleted
            ? [
              <Button key="back" onClick={onCancel}>
                {t(FONTS_UPLOAD_MODAL.CANCEL_FONT_UPLOAD_BUTTON.key, FONTS_UPLOAD_MODAL.CANCEL_FONT_UPLOAD_BUTTON.value)}
              </Button>,
              <Button
                key="submit"
                type="primary"
                disabled={!enabled || duplicate}
                loading={loader}
                onClick={() => saveFonts()}
              >
                {loader ? t(FONTS_UPLOAD_MODAL.SAVING_BUTTON.key, FONTS_UPLOAD_MODAL.SAVING_BUTTON.value) : t(FONTS_UPLOAD_MODAL.SAVE_UPLOADED_FONT_BUTTON.key, FONTS_UPLOAD_MODAL.SAVE_UPLOADED_FONT_BUTTON.value)}
              </Button>,
            ]
            : null
        }
        className="warning-modal"
      >
        {uploadCompleted ? (
          <Result
            status="success"
            title={t(FONTS_UPLOAD_MODAL.FONTS_UPLOADED_SUCCESS_MESSAGE.key, FONTS_UPLOAD_MODAL.FONTS_UPLOADED_SUCCESS_MESSAGE.value)}
            extra={
              <Button
                key="back"
                type="primary"
                onClick={() => {
                  setUpload(false);
                  onSucess();
                }}
              >
                {t(FONTS_UPLOAD_MODAL.MOVE_TO_UPLOAD_SECTION_BUTTON.key, FONTS_UPLOAD_MODAL.MOVE_TO_UPLOAD_SECTION_BUTTON.value)}
              </Button>
            }
          />
        ) : (
          <ModalBody>
            <ModalContainer width="90%">
              {tabIndex === 1 && renderGoogleFonts()}
              {tabIndex === 2 && renderUploadView()}
            </ModalContainer>
          </ModalBody>
        )}
      </Modal>
    </Container>
  );
};
export default UploadFontModal;
